/**
 * PricingBreakdown Component
 *
 * Detailed breakdown of booking costs with tooltips.
 */

import React from 'react';
import type { BookingPricing } from '../hooks/usePricing';

interface PricingBreakdownProps {
  pricing: BookingPricing;
  showDetails?: boolean;
}

export const PricingBreakdown: React.FC<PricingBreakdownProps> = ({
  pricing,
  showDetails = true,
}) => {
  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const regularNights = pricing.nights - pricing.weekendNights;

  return (
    <div className="space-y-3">
      {showDetails && (
        <>
          {/* Nightly rates breakdown */}
          {pricing.weekendNights > 0 && pricing.weekendRate ? (
            <>
              {/* Regular nights */}
              {regularNights > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>
                    {formatCurrency(pricing.nightlyRate)} x {regularNights} night
                    {regularNights !== 1 ? 's' : ''}
                  </span>
                  <span>{formatCurrency(pricing.nightlyRate * regularNights)}</span>
                </div>
              )}

              {/* Weekend nights */}
              <div className="flex justify-between text-gray-600">
                <span>
                  {formatCurrency(pricing.weekendRate)} x {pricing.weekendNights} weekend
                  night{pricing.weekendNights !== 1 ? 's' : ''}
                </span>
                <span>
                  {formatCurrency(pricing.weekendRate * pricing.weekendNights)}
                </span>
              </div>
            </>
          ) : (
            <div className="flex justify-between text-gray-600">
              <span className="underline decoration-dotted cursor-help">
                {formatCurrency(pricing.nightlyRate)} x {pricing.nights} night
                {pricing.nights !== 1 ? 's' : ''}
              </span>
              <span>{formatCurrency(pricing.nightlyTotal)}</span>
            </div>
          )}

          {/* Cleaning fee */}
          {pricing.cleaningFee > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Cleaning fee</span>
              <span>{formatCurrency(pricing.cleaningFee)}</span>
            </div>
          )}

          {/* Service fee - shown only when guest pays some/all of the commission */}
          {pricing.guestServiceFee > 0 && (
            <div className="flex justify-between text-gray-600">
              <span title={`${pricing.feeMode === 'split' ? 'Half of the' : ''} platform service fee`}>
                Service fee
              </span>
              <span>{formatCurrency(pricing.guestServiceFee)}</span>
            </div>
          )}

          {/* Taxes */}
          {pricing.taxes > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Taxes</span>
              <span>{formatCurrency(pricing.taxes)}</span>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 pt-3 mt-3" />
        </>
      )}

      {/* Total */}
      <div className="flex justify-between font-semibold text-lg">
        <span>Total ({pricing.currency})</span>
        <span>{formatCurrency(pricing.totalAmount)}</span>
      </div>

      {/* Security deposit note */}
      {pricing.securityDeposit > 0 && (
        <p className="text-sm text-gray-500 mt-2">
          + {formatCurrency(pricing.securityDeposit)} security deposit (refundable)
        </p>
      )}
    </div>
  );
};

export default PricingBreakdown;
